import { useState, type CSSProperties, type FormEvent } from 'react';
import { supabase } from '../supabase';
import { showToast } from './Toast';

/**
 * Shown after a user follows the password-reset link from their email. Supabase
 * fires a PASSWORD_RECOVERY event (App listens for it) which opens this over the
 * app; the user is in a temporary recovery session and sets a new password.
 */
export function PasswordResetModal({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Use at least 6 characters.');
      return;
    }
    setBusy(true);
    try {
      const { error } = await (supabase?.auth.updateUser({ password }) ??
        Promise.resolve({ error: new Error('Not configured') }));
      if (error) throw error;
      showToast('Password updated — you’re signed in', 'success');
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlay}>
      <form style={card} onSubmit={submit}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Set a new password</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
          Choose a new password for your account.
        </p>
        <input
          type="password"
          autoComplete="new-password"
          autoFocus
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={input}
        />
        {error && <div style={errorBox}>{error}</div>}
        <button type="submit" disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Saving…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10001,
  padding: 16,
};
const card: CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong, var(--panel-border))',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  padding: 20,
  width: 380,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  boxShadow: 'var(--shadow-lg)',
};
const input: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel-alt, var(--panel))',
  color: 'var(--text)',
  padding: '10px 12px',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const primaryBtn: CSSProperties = {
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  padding: '10px 14px',
  cursor: 'pointer',
};
const errorBox: CSSProperties = {
  fontSize: 12.5,
  color: '#fecaca',
  background: 'rgba(220,38,38,0.12)',
  border: '1px solid rgba(220,38,38,0.4)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 10px',
  lineHeight: 1.4,
};
